import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

export default function SuperadminCourseReviewsIndex({ reviews = [], filters = {} }) {
  const [query, setQuery] = useState(filters.q || '');

  const submit = (e) => {
    e.preventDefault();
    router.get('/superadmin/course_reviews', { q: query, status: filters.status || '' }, { preserveState: true, preserveScroll: true });
  };

  const setStatus = (status) => {
    router.get('/superadmin/course_reviews', { q: filters.q || '', status }, { preserveState: true, preserveScroll: true });
  };

  const clear = () => {
    setQuery('');
    router.get('/superadmin/course_reviews', {}, { preserveState: true, preserveScroll: true });
  };

  const total = reviews.length;
  const hasFilter = (filters.q || '').trim().length > 0 || !!filters.status;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Course Reviews</h1>
          <p className="mt-1 text-sm text-gray-500">Clinical reviewer feedback submitted for course content.</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search reviewer or course title…"
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer">
                Search
              </button>
              {hasFilter && (
                <button type="button" onClick={clear} className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer">
                  Clear
                </button>
              )}
            </div>
          </form>

          <div className="mt-4 flex gap-2">
            {[
              { key: '', label: 'All' },
              { key: 'submitted', label: 'Submitted' },
              { key: 'draft', label: 'Drafts' },
            ].map(opt => (
              <button
                key={opt.key || 'all'}
                onClick={() => setStatus(opt.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition cursor-pointer ${
                  (filters.status || '') === opt.key
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Reviews</h2>
            <span className="ml-auto text-sm text-gray-400">
              {total} {total === 1 ? 'review' : 'reviews'}
            </span>
          </div>

          {total === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {hasFilter ? 'No reviews match those filters.' : 'No reviews yet.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {reviews.map((r) => (
                <Link
                  key={r.id}
                  href={`/superadmin/course_reviews/${r.id}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 gap-3 first:pt-0 last:pb-0 group"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-medium text-gray-900 truncate group-hover:text-indigo-700">{r.reviewer_name || '—'}</p>
                      {r.credentials && (
                        <span className="text-xs text-gray-400">· {r.credentials}</span>
                      )}
                      {r.submitted ? (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium">
                          Submitted
                        </span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{r.course_title || '—'}</p>
                  </div>
                  <div className="shrink-0 text-right text-xs text-gray-400">
                    <div>Reviewed {r.review_date || r.created_at}</div>
                    <div>Updated {r.updated_at}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
